import { FC, useState, useEffect, useRef, useCallback } from 'react';
import {
	OrganisationResponse,
	SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH,
	SearchOrganisationByAdminResponse,
	SearchOrganizationByAdminRequest,
} from '@joc/api-gateway/lib/api-client';
import { API } from 'core/API';
import { useDebounceValue } from 'core/customHooks';

import styles from './InputSearchOrg.module.scss';

type InputSearchOrgProps = {
	placeholder: string;
	fieldName: string;
	fieldValue?: string;
	id?: string;
	classList?: Array<string>;
	parentClass?: string;
	value?: string;
	// eslint-disable-next-line no-unused-vars
	selectHandler: (value: string | null) => void;
	autoFocus?: boolean;
	userOrganizations?: Array<OrganisationResponse>;
};

const InputSearchOrg: FC<InputSearchOrgProps> = ({
	placeholder = '',
	id,
	fieldValue,
	selectHandler,
	userOrganizations,
}: // autoFocus = false,
InputSearchOrgProps) => {
	const [isDropOpen, setDropOpen] = useState(false);
	const [organizationList, setOrganizationList] =
		useState<Array<SearchOrganisationByAdminResponse | OrganisationResponse>>();
	const [isResultSelected, setResultSelected] = useState(false);

	const fetchOrganizations = useCallback((inputText?: string) => {
		return API.searchOrganizationsByAdmin(
			SearchOrganizationByAdminRequest.fromJS({
				...(inputText
					? {
							fullTextSearch: {
								value: inputText || '',
								fields: [SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH.Name],
							},
					  }
					: {}),
			})
		);
	}, []);

	const changeHandlerInput = async (inputText: string) => {
		if (isResultSelected) return;
		if (!inputText.length) selectHandler('');
		try {
			if (!userOrganizations) {
				const response = await fetchOrganizations(inputText);
				setOrganizationList(response.records);
			} else {
				setOrganizationList(
					userOrganizations
						.filter(org =>
							inputText ? org.organizationName.toLowerCase().includes(inputText.toLowerCase()) : org
						)
						.sort((a, b) => {
							if (a.organizationName < b.organizationName) {
								return -1;
							}
							if (a.organizationName > b.organizationName) {
								return 1;
							}
							return 0;
						})
				);
			}

			setDropOpen(true);
		} catch (error: any) {
			// eslint-disable-next-line no-console
			console.warn(error);
		}
	};

	const selectOrganization = (a: SearchOrganisationByAdminResponse | OrganisationResponse) => {
		setInputValue(a.organizationName);
		selectHandler(a.id);
		if (!userOrganizations) setOrganizationList([]);
		setDropOpen(false);
		setResultSelected(true);
	};

	useEffect(() => {
		const getFirstOptions = async () => {
			const res = await fetchOrganizations();
			setOrganizationList(res.records);
		};

		if (userOrganizations) setOrganizationList(userOrganizations);
		else getFirstOptions().finally();
	}, []);

	useEffect(() => {
		if (userOrganizations) {
			if (userOrganizations.length) {
				const defaultOrganization = userOrganizations.find(org => org.id === fieldValue);
				if (defaultOrganization) {
					setInputValue(defaultOrganization.organizationName);
					selectHandler(defaultOrganization.id);
					setDropOpen(false);
					setResultSelected(true);
					setOrganizationList(
						userOrganizations.sort((a, b) => {
							if (a.organizationName < b.organizationName) {
								return -1;
							}
							if (a.organizationName > b.organizationName) {
								return 1;
							}
							return 0;
						})
					);
				}
			} else {
				setInputValue('Private');
				selectHandler('Private');
				setDropOpen(false);
				setResultSelected(true);
			}
		}
	}, [userOrganizations, fieldValue]);

	const [inputValue, setInputValue] = useState<string | undefined>(undefined);

	const debouncedValue = useDebounceValue(inputValue, 500);

	const ref = useRef<HTMLInputElement>(null);

	const selectRef = useRef<HTMLDivElement>(null);

	const handleClickOutside = (event: Event) => {
		// isDropOpen && setFieldTouched(fieldName, true);
		if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
			setDropOpen(false);
		}
	};

	const setFocus = (): void => {
		if (ref && ref.current) {
			ref.current.focus();
		}
	};

	useEffect(() => {
		if (debouncedValue !== undefined) changeHandlerInput(debouncedValue).finally();
	}, [debouncedValue]);

	useEffect(() => {
		if (!fieldValue && inputValue !== undefined) setInputValue('');
	}, [fieldValue]);

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, true);
		return () => document.removeEventListener('click', handleClickOutside, true);
	}, [isDropOpen]);

	return (
		<div className={styles.input__search}>
			<div className={styles.input__search_icon} onClick={setFocus} />
			<input
				id={id && id}
				type="text"
				value={inputValue || ''}
				placeholder={placeholder}
				ref={ref}
				onChange={e => setInputValue(e.target.value)}
				onClick={() => {
					setResultSelected(false);
					if (userOrganizations || organizationList?.length) setDropOpen(true);
				}}
			/>
			{isDropOpen && !isResultSelected && (
				<div ref={selectRef} className={styles.dropWrap}>
					{organizationList && organizationList?.length > 0 ? (
						organizationList.map(el => (
							<div key={el.id} className={styles.dropDown} onClick={() => selectOrganization(el)}>
								{el.id === fieldValue ? (
									<>
										{el.organizationLogoPath && (
											<img
												className={styles.dropDown__image}
												src={`${process.env.REACT_APP_S3_API}/${el.organizationLogoPath}`}
												alt={el.organizationName}
											/>
										)}
										<b>{el.organizationName}</b>
									</>
								) : (
									<>
										{el.organizationLogoPath && (
											<img
												className={styles.dropDown__image}
												src={`${process.env.REACT_APP_S3_API}/${el.organizationLogoPath}`}
												alt={el.organizationName}
											/>
										)}
										<span>{el.organizationName}</span>
									</>
								)}
							</div>
						))
					) : (
						<div className={styles.dropDown}>no organization found</div>
					)}
				</div>
			)}
		</div>
	);
};

export default InputSearchOrg;
